import addDays from 'date-fns/add_days'
import compareAsc from 'date-fns/compare_asc'
import differenceInDays from 'date-fns/difference_in_calendar_days'
import formatDate from 'date-fns/format'
import get from 'lodash/get'
import isDateWithinRange from 'date-fns/is_within_range'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'
import parseDate from 'date-fns/parse'
import {createSelector} from 'reselect'
import sortBy from 'lodash/sortBy'

import * as api from './api'
import * as eventMgr from './eventMgr'
import * as events from './events'
import * as fit from './fit'
import * as looks from './looks'
import * as onboarding from '../components/Onboarding/redux/store'
import * as pdp from './productDetailPage'
import * as people from './people'
import * as router from './router'
import * as rtk from './rentToKeep'
import * as sizes from './sizes'
import * as user from './user'
import * as pcp from './productCategoryPage'
import {CategoryOrder} from './enumTS'
import {and, formatDateString, hasLength, or} from '../utils'
import {
  ATL_ORIGIN_PDP,
  ATL_ORIGIN_HTO,
  ATL_ORIGIN_BUY_EXPERIMENT_PDP,
  DAY_CUTOFF_TO_ADD_REMOVE_ITEMS,
  EVENT_IN_PROGRESS,
  STANDARD_DATE_FORMAT,
  TRANSACTION_TYPE_BUY,
  TRANSACTION_TYPE_HTO,
  TRANSACTION_TYPE_RENT,
} from '../const'

// Helpers

export const isDateInRange = (date) => (range) =>
  isDateWithinRange(parseDate(date), get(range, 'startDate', ''), get(range, 'endDate'))

export const currentDate = formatDateString(new Date(), STANDARD_DATE_FORMAT)

// Computed

// -- User

export const userIsOwner = createSelector(
  events.currentOwnerId,
  user.id,
  user.isAuthenticated,
  (eventOwnerId, userId, userIsAuthenticated) => !!(userIsAuthenticated && userId === eventOwnerId),
)

export const userIsParticipant = createSelector(
  events.currentId,
  events.currentOwnerId,
  user.id,
  user.isAuthenticated,
  (eventId, eventOwnerId, userId, userIsAuthenticated) =>
    !!(eventId && userIsAuthenticated && userId !== eventOwnerId),
)

export const customerType = createSelector(
  userIsOwner,
  userIsParticipant,
  (isOwner, isParticipant) => {
    if (isOwner) return 'Creator'
    if (isParticipant) return 'Participant'
    return 'None'
  },
)

export const userIsOwnerByEventId = createSelector(
  events.eventOwnerIdById,
  user.id,
  user.isAuthenticated,
  (eventOwnerId, userId, userIsAuthenticated) => !!(userIsAuthenticated && userId === eventOwnerId),
)

export const userIsParticipantByEventId = createSelector(
  events.eventIdById,
  events.eventOwnerIdById,
  user.id,
  user.isAuthenticated,
  (eventId, eventOwnerId, userId, userIsAuthenticated) =>
    !!(eventId && userIsAuthenticated && userId !== eventOwnerId),
)

export const isUserOrder = createSelector(
  eventMgr.userIdByOrderId,
  user.id,
  (userIdByOrderId, userId) => userIdByOrderId === userId,
)

export const canAddRemoveItems = createSelector(
  isUserOrder,
  eventMgr.eventDate,
  eventMgr.isEventCreatorOrderByOrderId,
  eventMgr.orderIsCancelledStatusByOrderId,
  eventMgr.orderTypePurchaseByOrderId,
  ($isUserOrder, date, isEventCreator, isCancelled, isPurchaseOrder) =>
    $isUserOrder &&
    isEventCreator &&
    !isPurchaseOrder &&
    differenceInDays(parseDate(date), new Date()) >= DAY_CUTOFF_TO_ADD_REMOVE_ITEMS &&
    !isCancelled,
)

export const canKeepRental = createSelector(
  eventMgr.orderTypePurchaseByOrderId,
  eventMgr.isShippedByOrderId,
  rtk.hasEligibleRtkItems,
  ($isPurchaseOrder, $orderIsShipped, $hasRtkItems) =>
    !$isPurchaseOrder && $orderIsShipped && $hasRtkItems,
)

// -- Looks

export const currentUserHasRentalItems = createSelector(
  people.user,
  looks.getNonHtoLooks,
  (userLookIds, nonHtoLooks) =>
    userLookIds &&
    nonHtoLooks
      .filter((look) => userLookIds.indexOf(look.id) >= 0)
      .some(
        (look) =>
          look.products.some((product) => product.transactionType === TRANSACTION_TYPE_RENT),
        // returns true when a rental product exists in a user look
      ),
)

export const participantLooksAdded = createSelector(people.participantsGroupedByLookId, hasLength)

export const selfLooksAdded = createSelector(people.user, hasLength)

// the user has a non-HTO look assigned to themself
export const hasSelfLooks = createSelector(
  selfLooksAdded,
  userIsParticipant, // assume every participant has look assigned to themself
  or,
)

export const userNeedsSizes = createSelector(
  looks.hasNoLooks, // at this point, we don't know the user's intention
  looks.hasHtoLooks, // if the user has an HTO, they need sizes
  hasSelfLooks, // if the user is renting a look for themself, they need sizes
  or,
)

/**
 * Checks to see if given look is a home try-on
 * Checks to see if current user is wearing given look
 * Gathers all participants wearing given look, ordered by ownerPays --> descending order places truthy first
 * Ordered participants array is looped over
 * Formats results into a new array
 * @param {String} id
 * @return {Array} formats all wearers (current user and any participants) of given look id
 */
export const allWearersByLookId = createSelector(
  looks.isHtoByLookId,
  people.isOwnerWearingLook,
  people.findParticipantsWearingLookByLookId,
  user.email,
  (isHto, owner, $participants, userEmail) => {
    const wearers = []
    if (isHto) {
      wearers.push({email: userEmail, isOwner: true, name: 'Your Home Try-On', ownerPays: true})
    }
    if (owner) {
      wearers.push({email: userEmail, isOwner: true, name: 'Your Look', ownerPays: true})
    }
    if ($participants) {
      orderBy($participants, ['ownerPays'], ['desc']).map((participant) =>
        wearers.push({
          email: participant.email,
          isOwner: false,
          name: `${participant.firstName} ${participant.lastName}`,
          ownerPays: participant.ownerPays,
        }),
      )
    }
    return wearers
  },
)

/**
 * returns true if given look id matches a look with at least one wearer
 * @param {String} id
 * @return {Boolean}
 */
export const lookIsAssignedByLookId = createSelector(allWearersByLookId, hasLength)

/**
 * Returns an array of all looks that do not have at least one wearer assigned
 * @return {Array}
 */
export const getLooksMissingWearerAssignment = (state) => {
  const allLooks = looks.looks(state)
  return allLooks.filter((look) => !lookIsAssignedByLookId(state, look.id))
}

/**
 * Returns true if any looks are missing wearer assignment
 * @return {Boolean}
 */
export const anyLookIsMissingWearerAssignment = createSelector(
  getLooksMissingWearerAssignment,
  hasLength,
)

/**
 * Returns a sorted array starting with HTO look, owner look, and participant look(s)
 *
 * @returns {Array}
 */
export const sortAllLooksByRole = createSelector(
  looks.looks,
  people.user,
  (allLooks, userLookId = []) => {
    const htoLook = ({hto}) => (hto ? -1 : 1)
    const ownersLook = ({id}) => (userLookId.includes(id) ? -1 : 1)
    // sort the looks by HTO then owner's look,
    return sortBy(allLooks, [htoLook, ownersLook])
  },
)

export const getAllLooksOrderedByProductCategory = createSelector(
  sortAllLooksByRole,
  (sortedLooks) =>
    sortedLooks.map((look) => ({
      ...look,
      ...{
        products: sortBy(look.products, ({category}) => CategoryOrder[category]),
      },
    })),
)

// -- Fit

export const isFitIntroOrSurvey = createSelector(
  router.currPageIsInFit,
  fit.currStepIsInSurvey,
  router.currPageIsFitIntro,
  (inFit, isInSurvey, isFitIntro) => (inFit && isInSurvey) || isFitIntro,
)

export const sizeInputsOverSizeData = createSelector(
  sizes.sizes,
  fit.sizeInputsById,
  (data, inputs) => ({...data, ...inputs}),
)

export const invalidSizeMessage = createSelector(
  sizes.hasInvalidSizeCombinationError,
  fit.isEditing,
  sizeInputsOverSizeData,
  (hasError, isEditing, inputs) => {
    switch (hasError && isEditing) {
      case 'jacket':
      case 'chest':
      case 'jacketLength':
        return `We do not carry a ${inputs.chest}${inputs.jacketLength} jacket. Please select another size.`
      case 'pants':
      case 'waist':
      case 'inseam':
        return `We do not carry a ${inputs.waist}/${inputs.inseam} pant. Please select another size.`
      case 'shirt':
      case 'neck':
      case 'sleeve':
        return `We do not carry a ${inputs.neck}/${inputs.sleeve} shirt. Please select another size.`
      case 'shoe':
        return `We do not carry a ${inputs.shoe} shoe. Please select another size.`
      default:
        return 'Please select a valid size.'
    }
  },
)

// if the user has a checked out event occurring within 30 days, they cannot change measurements,
//  unless they don't have sizes! wierd...
export const canChangeMeasurements = createSelector(
  sizes.exist,
  events.allEventsAreFetched,
  events.soonestUpcomingPaidEventDate,
  (sizesExist, haveAllEvents, date) => {
    const daysFromUpcomingPaidEventDate = differenceInDays(parseDate(date), new Date())
    return (
      !sizesExist || // if the user doesn't have sizes, for bejeezus sake let them edit them!
      (haveAllEvents &&
        // 30 or more days away or yesterday and earlier
        (!date || daysFromUpcomingPaidEventDate >= 30 || daysFromUpcomingPaidEventDate < 0))
    )
  },
)

// -- HTO

export const htoCategoryAndNoPastHto = createSelector(
  pcp.htoEnabledCategory,
  events.hasOtherHto,
  (isHtoEnabled, hasOtherHto) => isHtoEnabled && !hasOtherHto,
)

// -- Event

export const canDeleteEvent = createSelector(
  userIsOwnerByEventId,
  events.eventStatusById,
  events.isCurrentEvent,
  (isOwner, status, isCurrent) => isOwner && (status === EVENT_IN_PROGRESS || isCurrent),
)

export const isEventUpcoming = createSelector(
  eventMgr.eventIdByOrderId,
  events.bookedEvents,
  (eventId, bookedEvents) => bookedEvents.some((event) => event.id === eventId),
)

export const isPartyAvailable = createSelector(
  events.currentType,
  events.currentNumMembers,
  pdp.product,
  (eventType, eventNumMembers, product) => {
    if (!product) {
      // if product is null or undefined we're not on a proudct page.. return true
      return true
    }
    let partyAvailable = get(product, 'setSpecs.isPartyAvailable')
    if (!partyAvailable) {
      if (eventType === 0) {
        partyAvailable =
          eventNumMembers < 2 || eventNumMembers === undefined || eventNumMembers == null
      } else {
        partyAvailable = true
      }
    }
    return partyAvailable
  },
)

// -- Sold Out

/**
 * Returns the sold out date range for a rent item
 * @param {String} eventDate The event date
 * @param {Array} rentSoldOutPeriods An array of one ore more objects with an end_date and start_date for the soldout period
 * @return {String} Returns a string
 */
export const soldOutPeriodForRent = createSelector(
  events.currentDate,
  pdp.rentProductSoldOutPeriods,
  (eventDate, rentSoldOutPeriods = []) => {
    let result = ''
    if (eventDate && rentSoldOutPeriods) {
      const dateObj = rentSoldOutPeriods.find(isDateInRange(eventDate))
      if (dateObj) {
        result = `${formatDateString(dateObj.startDate, 'M/D')} \u2013 ${formatDateString(
          dateObj.endDate,
          'M/D',
        )}`
      }
    }
    return result
  },
)

// PDP Buy New Sold Out button will only display the date of when the product is back in stock
export const soldOutEndDateForBuy = createSelector(
  pdp.buyProductSoldOutPeriods,
  (buySoldOutPeriods) => {
    if (buySoldOutPeriods) {
      const endDate = buySoldOutPeriods.find(isDateInRange(currentDate))
      if (endDate) {
        const endDatePlusOne = addDays(endDate.endDate, 1)
        return endDatePlusOne ? `${formatDateString(endDatePlusOne, 'M/D')}` : ''
      }
    }
    return ''
  },
)

export const soldOutPeriodForTransactionType = createSelector(
  pdp.buttonTransactionType,
  soldOutPeriodForRent,
  soldOutEndDateForBuy,
  (buttonType, rentalSoldOutPeriod = '', buySoldOutEndDate = '') => {
    return {
      [TRANSACTION_TYPE_RENT]: rentalSoldOutPeriod,
      [TRANSACTION_TYPE_BUY]: buySoldOutEndDate,
    }[buttonType]
  },
)

export const isRentSoldOutForEvent = createSelector(
  events.currentDate,
  pdp.rentProductSoldOutPeriods,
  (eventDate, rentalSoldOutPeriod) =>
    !!(eventDate && rentalSoldOutPeriod && rentalSoldOutPeriod.some(isDateInRange(eventDate))),
)

export const isBuySoldOutForCurrentDate = createSelector(
  pdp.buyProductSoldOutPeriods,
  (buySoldOutPeriod) =>
    !!(currentDate && buySoldOutPeriod && buySoldOutPeriod.some(isDateInRange(currentDate))),
)

// -- Add to Look

export const isTransactionTypeButtonEnabled = createSelector(
  soldOutPeriodForTransactionType,
  pdp.isOfferedForTransactionType,
  (soldOutPeriods, isOffered) => !soldOutPeriods && isOffered,
)

export const selectedTransactionType = (state) => {
  const buy = isTransactionTypeButtonEnabled(state, {transactionType: 'buy'})
  const rent = isTransactionTypeButtonEnabled(state, {transactionType: 'rent'})

  if (!rent && buy) {
    return TRANSACTION_TYPE_BUY
  }
  if (rent && !buy) {
    return TRANSACTION_TYPE_RENT
  }
  if (!rent && !buy) {
    return null
  }
  return pdp.manuallySelectedTransactionType(state)
}

/**
 * Creates an array that the `addToLook` sagas use for adding a product to a
 * specific look.
 * @return {Array}
 */
export const addToLookProductsArray = createSelector(
  pdp.productCategorySlug,
  pdp.productName,
  pdp.productSetType,
  pdp.productSlug,
  selectedTransactionType,
  (categorySlug, name, setType, slug, transactionType) => [
    {categorySlug, name, origin: ATL_ORIGIN_PDP, setType, slug, transactionType},
  ],
)

/**
 * Creates an array that the `addToLook` sagas use for adding a product to a
 * specific look.
 * @return {Array}
 */
export const addToLookBuyExperimentProductsArray = createSelector(
  pdp.productCategorySlug,
  pdp.productName,
  pdp.productSetType,
  pdp.productSlug,
  selectedTransactionType,
  (categorySlug, name, setType, slug, transactionType) => [
    {categorySlug, name, origin: ATL_ORIGIN_BUY_EXPERIMENT_PDP, setType, slug, transactionType},
  ],
)

/**
 * Creates an array for adding a product on the PDP to a HTO. Specifically, we
 * include a 'hto' transaction type to inform our analytics. This transaction
 * type MUST be changed prior to sending to the server, however, as it will
 * cause errors.
 *
 * @return {Object} The appropriate object for adding a product to a HTO look
 */
export const addToHtoLookProductsArray = createSelector(
  pdp.productCategorySlug,
  pdp.productName,
  pdp.productSetType,
  pdp.productSlug,
  (categorySlug, name, setType, slug) => [
    {
      categorySlug,
      name,
      origin: ATL_ORIGIN_HTO,
      setType,
      slug,
      transactionType: TRANSACTION_TYPE_HTO,
    },
  ],
)

// -- Replacements

/**
 * Selector to determine if the current event date selected has already passed.
 *
 * @see [date-fns Date Comparison](https://date-fns.org/v1.30.1/docs/compareAsc)
 * @returns {Boolean} Returns true if event date is current day or in the past, false if event date is in the future.
 */
export const hasEventDatePassed = createSelector(
  eventMgr.eventDate,
  (eventDate) => compareAsc(new Date(eventDate), new Date()) <= 0,
)

/**
 * Selector to determine displaying the "Request Replacement" CTA on the order detail page.
 *
 * @returns {Boolean} Determines if we can make exchange requests for the order items on the order detail page
 */
export const canRequestReplacementOrderItem = createSelector(
  isUserOrder,
  isEventUpcoming,
  eventMgr.orderIsCancelledStatusByOrderId,
  eventMgr.isHtoByOrderId,
  eventMgr.isShippedByOrderId,
  hasEventDatePassed,
  ($isUserOrder, $isEventUpcoming, isCancelled, isHto, hasCurrentOrderShipped, eventDatePassed) =>
    $isUserOrder &&
    $isEventUpcoming &&
    !isCancelled &&
    !isHto &&
    hasCurrentOrderShipped &&
    !eventDatePassed,
)

// -- Onboarding

export const onboardingIsComplete = createSelector(
  onboarding.currentStepIsEventSummary,
  user.isAuthenticated,
  events.currentEventHasEventDetails,
  (currentStepIsEventSummary, userIsAuthenticated, eventHasDetails) =>
    !!(
      currentStepIsEventSummary || // onboarding was successful OR
      (userIsAuthenticated && eventHasDetails)
    ), // user is authenticated and has event details
)

// -- Groom Rents Free

export const isGroomRentsFreeTargetCustomer = createSelector(
  userIsOwner,
  looks.hasNonHtoLooks,
  events.currentTypeIsWedding,
  and,
)

// -- Soonest Event Date

// just in case we don't have a response from the API yet
export const DEFAULT_SOONEST_EVENT_DATE_GROUP = formatDate(
  addDays(new Date(), 10),
  STANDARD_DATE_FORMAT,
)

export const DEFAULT_SOONEST_EVENT_DATE_INDIVIDUAL = formatDate(
  addDays(new Date(), 6),
  STANDARD_DATE_FORMAT,
)

export const soonestEventDateGroup = (state) => get(state, 'api.soonestEventDate.data.group')
export const soonestEventDateIndividual = (state) =>
  get(state, 'api.soonestEventDate.data.individual')

export const soonestEventDate = createSelector(
  api.soonestEventDate.isSuccess,
  people.hasParticipants,
  soonestEventDateGroup,
  soonestEventDateIndividual,
  (success, hasParticipants, group, individual) => {
    if (success) {
      return hasParticipants ? group : individual
    }
    return hasParticipants
      ? DEFAULT_SOONEST_EVENT_DATE_GROUP
      : DEFAULT_SOONEST_EVENT_DATE_INDIVIDUAL
  },
)

// Args

// get the look corresponding to the given lookId
// check for equality between the look payer and the userId
// if they are NOT equal, the look is being paid for by someone else
export const lookIsPaidFor = (state, lookId) =>
  !isEqual(get(looks.getLookById(state, lookId), 'payer'), user.id(state))
